"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { toast } from "sonner";
import type { AuctionItem } from "@/features/auctions/types";
import { announce } from "@/lib/a11y/announce";
import { useSingleFlight, type TransactionRecord } from "@/lib/transaction-flow";
import { setBidWinner, updateBidStatus } from "./api";
import type { BidItem } from "./types";
import { getErrorMessage } from "@/lib/error-message";

/* eslint-disable @typescript-eslint/no-explicit-any -- the axios error shape, as it was in AcceptBidFlow */

export type BidDecision = "accept" | "reject" | "set-winner";

/**
 * The owner's decision on one bid — AcceptBidFlow's flow, moved here
 * unchanged: no input step (the bid already exists), straight to a review
 * and one confirm; `updateBidStatus` accepted / rejected, or `setBidWinner`;
 * one request per confirm; the result is recorded either way.
 *
 * CHOOSING THE WINNER STARTS THE SETTLEMENT, IT DOES NOT FINISH IT: set-winner
 * always answers at once with the job queued (`SUBMIT_SET_WINNER` / `settling`), so
 * the receipt says «settling» and `onQueued` hands that to the page, which then
 * follows the job on the socket (features/auctions/settlement).
 */
export function useBidDecision(t0) {
  const $ = _c(32);
  const {
    auction,
    bid,
    mode,
    lang,
    onRecord,
    onSuccess,
    onQueued
  } = t0;
  const isFa = lang === "fa";
  const [step, setStep] = useState("review");
  const [result, setResult] = useState(null);
  const {
    run,
    pending
  } = useSingleFlight();
  let t1;
  if ($[0] !== auction || $[1] !== bid.currency?.symbol) {
    t1 = (bid.currency?.symbol || auction.currency?.symbol || "").toUpperCase() || null;
    $[0] = auction;
    $[1] = bid.currency?.symbol;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currencySymbol = t1;
  const bidderName = bid.user?.name || bid.user?.username || (isFa ? "\u06A9\u0627\u0631\u0628\u0631" : "User");
  const isReject = mode === "reject";
  const isSetWinner = mode === "set-winner";
  const flow = isReject ? "reject-bid" : isSetWinner ? "set-bid-winner" : "accept-bid";
  let t2;
  if ($[3] !== bid.id || $[4] !== bid.price || $[5] !== bidderName || $[6] !== currencySymbol || $[7] !== flow || $[8] !== isFa || $[9] !== isReject || $[10] !== isSetWinner || $[11] !== onQueued || $[12] !== onRecord || $[13] !== onSuccess || $[14] !== run) {
    t2 = async function confirm() {
      const flight = await run(async requestId => {
        let queued = false;
        if (isReject) {
          await updateBidStatus(bid.id, {
            status: "rejected"
          });
        } else {
          if (isSetWinner) {
            await setBidWinner(bid.id, {
              is_win: true
            });
            queued = true;
          } else {
            await updateBidStatus(bid.id, {
              status: "accepted"
            });
          }
        }
        return {
          requestId,
          queued
        };
      });
      if (flight.skipped) {
        return;
      }
      const {
        requestId: requestId_0,
        queued: queued_0
      } = flight.result;
      const record = {
        requestId: requestId_0,
        flow,
        outcome: "success",
        title: isReject ? isFa ? "\u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0631\u062F \u0634\u062F" : "Bid rejected" : queued_0 ? isFa ? "\u0628\u0631\u0646\u062F\u0647 \u0627\u0646\u062A\u062E\u0627\u0628 \u0634\u062F \u2014 \u062F\u0631 \u062D\u0627\u0644 \u062A\u0633\u0648\u06CC\u0647" : "Winner chosen \u2014 settling" : isFa ? "\u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u062A\u0627\u06CC\u06CC\u062F \u0634\u062F" : "Bid accepted",
        description: `${bidderName} — ${bid.price}${currencySymbol ? ` ${currencySymbol}` : ""}${queued_0 ? isFa ? ". \u0627\u0646\u062A\u0642\u0627\u0644 \u0627\u062B\u0631 \u0648 \u067E\u0631\u062F\u0627\u062E\u062A \u0631\u0648\u06CC \u0634\u0628\u06A9\u0647 \u062F\u0631 \u062C\u0631\u06CC\u0627\u0646 \u0627\u0633\u062A\u061B \u067E\u0627\u06CC\u0627\u0646\u0634 \u0647\u0645\u06CC\u0646\u200C\u062C\u0627 \u062E\u0628\u0631 \u062F\u0627\u062F\u0647 \u0645\u06CC\u200C\u0634\u0648\u062F." : ". The transfer and the payment are running on chain; you'll be told here when they finish." : ""}`,
        at: new Date().toISOString()
      };
      setResult(record);
      setStep("result");
      onRecord(record);
      if (queued_0) {
        onQueued?.();
      }
      await onSuccess();
      toast.success(record.title as string);
      announce(`${record.title} — ${record.description}`, "polite");
    };
    $[3] = bid.id;
    $[4] = bid.price;
    $[5] = bidderName;
    $[6] = currencySymbol;
    $[7] = flow;
    $[8] = isFa;
    $[9] = isReject;
    $[10] = isSetWinner;
    $[11] = onQueued;
    $[12] = onRecord;
    $[13] = onSuccess;
    $[14] = run;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const confirm = t2;
  let t3;
  if ($[16] !== confirm || $[17] !== flow || $[18] !== isFa || $[19] !== isReject || $[20] !== isSetWinner || $[21] !== onRecord) {
    t3 = async function submit() {
      ;
      try {
        await confirm();
      } catch (t4) {
        const error = t4;
        const message = getErrorMessage(error, "") || undefined;
        const failure = {
          requestId: `local-${Date.now()}`,
          flow,
          outcome: "failure",
          title: isReject ? isFa ? "\u0631\u062F \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0627\u0646\u062C\u0627\u0645 \u0646\u0634\u062F" : "The bid was not rejected" : isSetWinner ? isFa ? "\u062A\u0639\u06CC\u06CC\u0646 \u0628\u0631\u0646\u062F\u0647 \u0627\u0646\u062C\u0627\u0645 \u0646\u0634\u062F" : "The winner was not set" : isFa ? "\u062A\u0627\u06CC\u06CC\u062F \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0627\u0646\u062C\u0627\u0645 \u0646\u0634\u062F" : "The bid was not accepted",
          description: message || (isFa ? "\u0686\u06CC\u0632\u06CC \u062A\u063A\u06CC\u06CC\u0631 \u0646\u06A9\u0631\u062F. \u0645\u06CC\u200C\u062A\u0648\u0627\u0646\u06CC\u062F \u062F\u0648\u0628\u0627\u0631\u0647 \u062A\u0644\u0627\u0634 \u06A9\u0646\u06CC\u062F." : "Nothing changed. You can try again."),
          at: new Date().toISOString()
        };
        onRecord(failure);
        setResult(failure);
        setStep("result");
        toast.error(failure.title as string);
        announce(`${failure.title} — ${failure.description}`, "assertive");
      }
    };
    $[16] = confirm;
    $[17] = flow;
    $[18] = isFa;
    $[19] = isReject;
    $[20] = isSetWinner;
    $[21] = onRecord;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  const submit = t3;
  let t4;
  if ($[23] !== bidderName || $[24] !== currencySymbol || $[25] !== isReject || $[26] !== isSetWinner || $[27] !== pending || $[28] !== result || $[29] !== step || $[30] !== submit) {
    t4 = {
      step,
      result,
      pending,
      submit,
      currencySymbol,
      bidderName,
      isReject,
      isSetWinner
    };
    $[23] = bidderName;
    $[24] = currencySymbol;
    $[25] = isReject;
    $[26] = isSetWinner;
    $[27] = pending;
    $[28] = result;
    $[29] = step;
    $[30] = submit;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  return t4;
}